import React, { useEffect, useState, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Users, MessageSquare, UserX, Ban, Shield, ShieldOff, ChevronDown, Loader2,
  MapPin, Pickaxe, Compass, Gamepad2, Eye, Heart, Drumstick, Clock, Skull, Sparkles, Copy,
} from 'lucide-react';
import { serverStore } from "../../stores/server.store";
import { userStore } from "../../stores/user.store";

import toast from 'react-hot-toast';
import { PlayerAvatar } from './PlayerAvatar';
import { Card } from '../design/Card';
import { Button } from '../design/Button';
import { Input } from '../design/Input';
import { Badge } from '../design/Badge';
import { Select } from '../design/Select';
import { cn } from '@/utils/cn';
import { apiFetch } from '../../utils/api';
import { BannedPlayersModal } from '../modals/BannedPlayersModal';
import { KnownPlayersModal } from './KnownPlayers';
import { can } from '@/utils/permissions';

export interface LivePlayer {
  name: string;
  uuid: string | null;
  health: number | null;
  food: number | null;
  xpLevel: number | null;
  pos: { x: number; y: number; z: number } | null;
  dimension: string | null;
  deaths?: number;
  playerKills?: number;
  mobKills?: number;
  sessionStartedAt: number | null;
}

const DIMENSION_META: Record<string, { label: string; cls: string }> = {
  'minecraft:overworld': { label: 'Oberwelt', cls: 'bg-green-500/10 text-green-400 border-green-500/20' },
  'minecraft:the_nether': { label: 'Nether', cls: 'bg-red-500/10 text-red-400 border-red-500/20' },
  'minecraft:the_end': { label: 'End', cls: 'bg-purple-500/10 text-purple-400 border-purple-500/20' },
};

export const dimensionMeta = (dim: string | null) => {
  if (!dim) return null;
  return DIMENSION_META[dim] || {
    // Modded-Dimensionen (z.B. "allthemodium:mining") lesbar machen
    label: dim.split(':').pop()!.replace(/_/g, ' '),
    cls: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  };
};

const sessionDuration = (startedAt: number | null) => {
  if (!startedAt) return null;
  const min = Math.floor((Date.now() - startedAt) / 60000);
  if (min < 60) return `${min} min`;
  return `${Math.floor(min / 60)}h ${min % 60}m`;
};

/** Kleine Stat-Zelle mit Icon, Label und Wert/Bar. */
const StatCell = ({ icon, label, children }: { icon: React.ReactNode; label: string; children: React.ReactNode }) => (
  <div className="bg-black/25 border border-line rounded-lg px-2.5 py-2 min-w-0">
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-fg-muted font-bold mb-1">
      {icon} {label}
    </div>
    {children}
  </div>
);

const StatBar = ({ value, max, barCls, }: { value: number; max: number; barCls: string }) => (
  <div className="flex items-center gap-2">
    <span className="text-sm font-bold text-white tabular-nums shrink-0">{Math.round(value)}<span className="text-fg-muted font-normal">/{max}</span></span>
    <div className="flex-1 h-1.5 rounded-full bg-white/10 overflow-hidden">
      <div className={cn('h-full rounded-full transition-all duration-500', barCls)} style={{ width: `${Math.max(0, Math.min(100, (value / max) * 100))}%` }} />
    </div>
  </div>
);

export const PlayerList = observer(() => {
  const { activeServer } = serverStore;
  const { currentUser } = userStore;
  const hasPermission = can(currentUser, 'can_moderate_server');
  const isSteam = String(activeServer?.type || '').startsWith('steam');

  const [loading, setLoading] = useState(false);
  const [expandedPlayer, setExpandedPlayer] = useState<string | null>(null);
  const [showBannedModal, setShowBannedModal] = useState(false);
  const [showKnownModal, setShowKnownModal] = useState(false);
  const [whisperMsg, setWhisperMsg] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [tpSelectionMode, setTpSelectionMode] = useState<string | null>(null);
  const [selectedSourcePlayer, setSelectedSourcePlayer] = useState<string>('@s');
  const [live, setLive] = useState<Map<string, LivePlayer>>(new Map());

  const fetchPlayers = useCallback(async () => {
    if (!activeServer || activeServer.status !== 'online') return;
    await serverStore.fetchRuntimeDetails(activeServer.id);
    // Live-Details (Health/Hunger/Pos/...) dazuladen - eigener Endpoint,
    // damit die schnelle "list"-Abfrage davon unabhaengig bleibt.
    try {
      const res = await apiFetch(`/api/servers/${activeServer.id}/players/live`);
      const data = await res.json();
      if (data.success) {
        setLive(new Map((data.players as LivePlayer[]).map((p) => [p.name, p])));
      }
    } catch { /* Live-Daten sind optional */ }
  }, [activeServer?.id, activeServer?.status]);

  useEffect(() => {
    if (activeServer?.status === 'online') {
      setLoading(true);
      fetchPlayers().finally(() => setLoading(false));
      const interval = setInterval(fetchPlayers, 10000);
      return () => clearInterval(interval);
    }
  }, [activeServer?.id, activeServer?.status, fetchPlayers]);

  const playerData = serverStore.serverRuntimeDetails.get(activeServer?.id || '')?.playerCount
    ? { ...serverStore.serverRuntimeDetails.get(activeServer?.id || '')!.playerCount }
    : { current: 0, max: 20, players: [] };

  const executeAction = async (action: string, player: string) => {
    if (!activeServer) return;
    setActionLoading(`${action}-${player}`);

    const res = await serverStore.executePlayerAction(activeServer.id, action, player, whisperMsg);

    if (res.success) {
      toast.success(`${action} für ${player} ausgeführt`);
      if (action === 'kick' || action === 'ban') fetchPlayers();
    } else {
      toast.error(res.message || 'Aktion fehlgeschlagen');
    }
    setActionLoading(null);
    setWhisperMsg('');
  };

  const executeCommand = async (command: string, successMsg: string) => {
    if (!activeServer) return;
    const res = await serverStore.executeConsoleCommand(activeServer.id, command);
    if (res.success) {
      toast.success(successMsg);
    } else {
      toast.error(res.message || 'Fehler');
    }
  };

  const copyPos = (p: LivePlayer) => {
    if (!p.pos) return;
    navigator.clipboard.writeText(`${p.pos.x} ${p.pos.y} ${p.pos.z}`);
    toast.success('Koordinaten kopiert');
  };

  if (!activeServer || activeServer.status !== 'online') return null;

  const gamemodes = [
    { id: 'survival', icon: Pickaxe, label: 'Survival', colorClass: 'text-green-400 border-green-500/20 hover:bg-green-500/10' },
    { id: 'creative', icon: Compass, label: 'Creative', colorClass: 'text-blue-400 border-blue-500/20 hover:bg-blue-500/10' },
    { id: 'adventure', icon: Gamepad2, label: 'Adventure', colorClass: 'text-yellow-400 border-yellow-500/20 hover:bg-yellow-500/10' },
    { id: 'spectator', icon: Eye, label: 'Spectator', colorClass: 'text-ember border-ember/20 hover:bg-ember/10' },
  ];

  return (
    <Card className="flex flex-col h-full" noPadding>
      <div className="bg-white/5 px-6 py-4 border-b border-line flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Users size={20} className="text-blue-400" />
          <span className="font-bold text-white text-lg">Spieler Online</span>
        </div>
        <div className="flex items-center space-x-3">
          <Button
            variant="outline"
            size="sm"
            className="h-8 px-2 text-xs border-blue-500/20 text-blue-400 hover:bg-blue-500/10 flex items-center gap-1"
            onClick={() => setShowKnownModal(true)}
          >
            <Users size={12} /> Bekannte
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-8 px-2 text-xs border-red-500/20 text-red-400 hover:bg-red-500/10 flex items-center gap-1"
            onClick={() => setShowBannedModal(true)}
          >
            <Ban size={12} /> Gebannt
          </Button>
          {loading && <Loader2 size={14} className="animate-spin text-fg-muted" />}
          <Badge variant="default" className="text-sm px-2.5 py-1">
            {playerData.current}/{playerData.max}
          </Badge>
        </div>
      </div>

      <div className="p-4 flex-1 overflow-y-auto max-h-[70vh] custom-scrollbar">
        {playerData.players.length === 0 ? (
          <div className="text-center py-12 text-fg-muted text-sm flex flex-col items-center">
            <Users size={32} className="mb-3 opacity-20" />
            Keine Spieler online
          </div>
        ) : (
          <div className="space-y-3">
            {playerData.players.map((player) => {
              const lp = live.get(player);
              const dim = dimensionMeta(lp?.dimension ?? null);
              const session = sessionDuration(lp?.sessionStartedAt ?? null);
              return (
                <div key={player} className="bg-black/20 border border-line rounded-xl overflow-hidden transition-colors hover:border-line-strong">
                  {/* Kopfzeile */}
                  <div
                    onClick={() => setExpandedPlayer(expandedPlayer === player ? null : player)}
                    className="hover:bg-white/5 p-3 flex items-center justify-between cursor-pointer transition-colors"
                  >
                    <div className="flex items-center space-x-3 min-w-0">
                      <div className="relative shrink-0">
                        <PlayerAvatar name={player} size={40} steam={isSteam} className="rounded-lg shadow-md" />
                        <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-online border-2 border-ink" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-white font-bold text-sm tracking-wide truncate">{player}</span>
                          {dim && (
                            <span className={cn('text-[9px] px-1.5 py-0.5 rounded border font-bold uppercase tracking-wide shrink-0', dim.cls)}>
                              {dim.label}
                            </span>
                          )}
                        </div>
                        {session && (
                          <div className="text-[11px] text-fg-muted flex items-center gap-1 mt-0.5">
                            <Clock size={10} /> Session: {session}
                          </div>
                        )}
                      </div>
                    </div>
                    <ChevronDown
                      size={18}
                      className={cn("text-fg-muted transition-transform duration-200 shrink-0", expandedPlayer === player && "rotate-180")}
                    />
                  </div>

                  {/* Live-Stats */}
                  {lp && (
                    <div className="px-3 pb-3 grid grid-cols-2 md:grid-cols-3 gap-2">
                      {lp.health !== null && (
                        <StatCell icon={<Heart size={11} className="text-red-400" />} label="Leben">
                          {/* Modded Server erlauben >20 HP (z.B. Craftoria) */}
                          <StatBar value={lp.health} max={Math.max(20, Math.ceil(lp.health))} barCls={lp.health <= 6 ? 'bg-red-500' : 'bg-green-500'} />
                        </StatCell>
                      )}
                      {lp.food !== null && (
                        <StatCell icon={<Drumstick size={11} className="text-orange-400" />} label="Hunger">
                          <StatBar value={lp.food} max={20} barCls="bg-orange-400" />
                        </StatCell>
                      )}
                      {lp.pos && (
                        <StatCell icon={<MapPin size={11} className="text-blue-400" />} label="Position">
                          <button
                            onClick={(e) => { e.stopPropagation(); copyPos(lp); }}
                            title="Koordinaten kopieren"
                            className="text-xs font-mono text-fg hover:text-blue-400 transition-colors flex items-center gap-1.5 truncate"
                          >
                            <span className="truncate">{lp.pos.x} / {lp.pos.y} / {lp.pos.z}</span>
                            <Copy size={10} className="shrink-0 opacity-50" />
                          </button>
                        </StatCell>
                      )}
                      {typeof lp.deaths === 'number' && (
                        <StatCell icon={<Skull size={11} className="text-fg-muted" />} label="Tode">
                          <span className="text-sm font-bold text-white tabular-nums">{lp.deaths}</span>
                        </StatCell>
                      )}
                      {lp.xpLevel !== null && (
                        <StatCell icon={<Sparkles size={11} className="text-yellow-400" />} label="XP-Level">
                          <span className="text-sm font-bold text-white tabular-nums">{Math.round(lp.xpLevel)}</span>
                        </StatCell>
                      )}
                      {typeof lp.mobKills === 'number' && (
                        <StatCell icon={<Pickaxe size={11} className="text-green-400" />} label="Mob-Kills">
                          <span className="text-sm font-bold text-white tabular-nums">{lp.mobKills}</span>
                        </StatCell>
                      )}
                    </div>
                  )}

                  {/* Gamemode/TP/Kick/Ban/OP sind Minecraft-Befehle (Name-basiert) - bei
                      Steam-Spielen wie ARK wirkungslos, dort ein Hinweis auf die Konsole. */}
                  {expandedPlayer === player && hasPermission && isSteam && (
                    <div className="border-t border-line p-4 text-xs text-fg-muted bg-black/40">
                      Spieler-Aktionen gehen bei diesem Spiel über die Konsole: <code className="text-fg">ListPlayers</code> zeigt
                      die Steam-ID, dann <code className="text-fg">KickPlayer &lt;ID&gt;</code> bzw. <code className="text-fg">BanPlayer &lt;ID&gt;</code>.
                    </div>
                  )}
                  {expandedPlayer === player && hasPermission && !isSteam && (
                    <div className="border-t border-line p-4 space-y-4 bg-black/40 animate-in slide-in-from-top-2 duration-200">
                      {/* Whisper */}
                      <div className="space-y-2">
                        <label className="text-[10px] text-fg-muted font-bold uppercase tracking-wider block ml-1">Nachricht senden</label>
                        <div className="flex items-center gap-2">
                          <Input
                            placeholder="Nachricht..."
                            value={whisperMsg}
                            onChange={(e) => setWhisperMsg(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && executeAction('whisper', player)}
                            className="flex-1"
                          />
                          <Button
                            onClick={() => executeAction('whisper', player)}
                            disabled={actionLoading === `whisper-${player}` || !whisperMsg.trim()}
                            size="icon"
                            variant="primary"
                          >
                            <MessageSquare size={16} />
                          </Button>
                        </div>
                      </div>

                      {/* Gamemode */}
                      <div className="space-y-2">
                        <label className="text-[10px] text-fg-muted font-bold uppercase tracking-wider block ml-1">Spielmodus</label>
                        <div className="grid grid-cols-2 gap-2">
                          {gamemodes.map((gm) => {
                            const Icon = gm.icon;
                            return (
                              <Button
                                key={gm.id}
                                variant="outline"
                                size="sm"
                                className={cn("w-full justify-start", gm.colorClass)}
                                onClick={() => executeCommand(`gamemode ${gm.id} ${player}`, `${player} → ${gm.label}`)}
                              >
                                <Icon size={14} className="mr-2" /> {gm.label}
                              </Button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="space-y-2">
                        <label className="text-[10px] text-fg-muted font-bold uppercase tracking-wider block ml-1">Aktionen</label>
                        <div className="grid grid-cols-2 gap-2">
                          {tpSelectionMode === player ? (
                            <div className="col-span-2 bg-black/40 p-2 rounded-lg border border-line animate-in slide-in-from-left-2">
                              <label className="text-[10px] text-fg-muted font-bold uppercase tracking-wider block mb-1">Wen zu {player} teleportieren?</label>
                              <div className="flex gap-2">
                                <Select
                                  className="flex-1"
                                  buttonClassName="text-xs px-2 py-1.5"
                                  value={selectedSourcePlayer}
                                  onChange={setSelectedSourcePlayer}
                                  options={[
                                    { value: "@s", label: "Mich (@s)" },
                                    ...playerData.players.filter(p => p !== player).map(p => ({ value: p, label: p })),
                                  ]}
                                />
                                <Button
                                  size="sm"
                                  variant="primary"
                                  className="h-7 px-2"
                                  onClick={() => {
                                    executeCommand(`tp ${selectedSourcePlayer} ${player}`, `Teleportiert ${selectedSourcePlayer} zu ${player}`);
                                    setTpSelectionMode(null);
                                  }}
                                >
                                  TP
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 px-2 text-fg-muted hover:text-white"
                                  onClick={() => setTpSelectionMode(null)}
                                >
                                  X
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <Button
                              variant="secondary"
                              size="sm"
                              className="w-full justify-start text-ember bg-ember/10 hover:bg-ember/20 border-ember/20"
                              onClick={() => {
                                setTpSelectionMode(player);
                                setSelectedSourcePlayer('@s');
                              }}
                            >
                              <MapPin size={14} className="mr-2" /> TP zu
                            </Button>
                          )}
                          <Button
                            variant="danger"
                            size="sm"
                            className="w-full justify-start"
                            disabled={!!actionLoading}
                            onClick={() => executeAction('kick', player)}
                          >
                            <UserX size={14} className="mr-2" /> Kick
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            className="w-full justify-start border-red-500/50 bg-red-500/10 hover:bg-red-500/20 text-red-500"
                            disabled={!!actionLoading}
                            onClick={() => executeAction('ban', player)}
                          >
                            <Ban size={14} className="mr-2" /> Ban
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full justify-start text-green-400 border-green-500/20 hover:bg-green-500/10"
                            disabled={!!actionLoading}
                            onClick={() => executeAction('op', player)}
                          >
                            <Shield size={14} className="mr-2" /> OP
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            className="col-span-2 w-full justify-center"
                            disabled={!!actionLoading}
                            onClick={() => executeAction('deop', player)}
                          >
                            <ShieldOff size={14} className="mr-2" /> Berechtigungen entfernen (DeOP)
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      <BannedPlayersModal
        isOpen={showBannedModal}
        onClose={() => setShowBannedModal(false)}
        serverId={activeServer.id}
      />
      <KnownPlayersModal
        isOpen={showKnownModal}
        onClose={() => setShowKnownModal(false)}
        serverId={activeServer.id}
      />
    </Card>
  );

});
